import { FundingFactory, FundingKey, FundingType } from '@bytel/leviator';
import type { ModesFinancement, ModesProduitFinancement } from '@bytel/pt-ihm-api-portailvente-sapic-catalogue';

import { getAppConfig } from '@services/config';

import { VapiMapper } from '@mappers/vapi';

import { JourneyType, Promotion, SapicProductType, SimulatedCartType } from '@app-types/api/sapic';
import { DiscountEnumType, DiscountType } from '@app-types/discount';
import { IFundingDetail } from '@app-types/funding';
import { FaiType, MobilePlanType, ObligationType, OptionType, RenewDelayType } from '@app-types/plan';
import { PRICE_TYPE } from '@app-types/price';
import { Product, SimulatedCartProduct } from '@app-types/product';
import { SimulatedQuote } from '@app-types/simulator/virtual-cart';

import { PlanRange, PlanSubTypeId, PlanTypeId } from '@constants/plan';
import { PLAY, TECHNOLOGY } from '@constants/provider';

const appConfig = getAppConfig();

export class SapicMapper {
    static mapToProduct(sapicProduct: SapicProductType) {
        const fundings =
            sapicProduct?.modesDeFinancement.map((fundingMode) =>
                SapicMapper.fundingMapper(fundingMode, sapicProduct.promotions),
            ) ?? [];
        const funding = fundings?.[0];
        const price = funding?.initialDeposit ?? 0;

        const simulatedCart = sapicProduct.simulation;

        return {
            id: sapicProduct.url,
            gencode: sapicProduct.gencode,
            urlKey: sapicProduct.url,
            url: sapicProduct.url,
            inStock: sapicProduct.estEnStock,
            brand: sapicProduct.marque,
            name: sapicProduct.nom,
            image: sapicProduct.image,
            medias: sapicProduct.medias,
            categories: sapicProduct.categories,
            stickers: sapicProduct.etiquetteAnimCo
                ? // Use VariantState.INFO to display the sticker when trilogy fix server component
                  [{ label: sapicProduct.etiquetteAnimCo, color: 'INFO' as const }]
                : undefined,
            price,
            recommended: sapicProduct.recommande ?? false,
            details: {
                price: {
                    initial: sapicProduct.prix.initial,
                    subsidized: sapicProduct.prix.subventionne,
                    forever: sapicProduct.prix.pourToujours,
                    final: sapicProduct.prix.final,
                },
                // v4
                discounts: sapicProduct.promotions.map(SapicMapper.discountMapper),
                fundings,
            },
            colors: sapicProduct?.couleurs ?? [],
            simulatedCart: SapicMapper.getSimulatedCart(simulatedCart),
            neededElements: sapicProduct?.necessiteElements?.flatMap((el) =>
                el.alternatives
                    ?.filter((alt) => alt.identifiant)
                    ?.map((alt) => ({ gencode: alt.identifiant, label: alt.libelle })),
            ), // need label for futur usage ?
        };
    }

    static createSimulatedQuote(quote: JourneyType): SimulatedQuote {
        return {
            products: [],
            type: quote.typeParcours,
            current: quote.statut === 'EN_COURS',
        };
    }

    static createSimulatedProducts(quote: JourneyType): Product[] {
        const products: Product[] = [];
        quote.elements.forEach((product) => {
            const simulatedProduct: Partial<Product> = {
                gencode: product.composantCommercial.identifiant,
                details: {
                    price: {
                        initial: product.prixAffiche?.prixCatalogue ?? 0,
                        final: product.prixAffiche?.prixAvecPromotions ?? 0,
                        forever: product.prixAffiche?.prixPourToujours ?? 0,
                        subsidized: product.prixAffiche?.prixPourToujours,
                        type: product.prixAffiche?.type as PRICE_TYPE,
                    },
                    discounts: product.promotionsCalculees?.promotionsAppliquees.map(VapiMapper.discountMapper) ?? [],
                    fundings: [],
                },
            };
            products.push(simulatedProduct as Product);
        });

        return products;
    }

    static getSimulatedCart(simulation: SimulatedCartType): SimulatedCartProduct | undefined {
        if (!simulation?.panier.parcours) {
            return undefined;
        }

        const simulatedCart: SimulatedCartProduct = {
            fundings: [],
            quotes: [],
        };

        simulation.panier.parcours.forEach((quote) => {
            const simulatedQuote: SimulatedQuote = SapicMapper.createSimulatedQuote(quote);

            simulatedQuote.products = SapicMapper.createSimulatedProducts(quote);
            simulatedQuote.products.forEach((product) => {
                const findParcoursForProduct = simulation.financements?.parcours?.find((p) =>
                    p.elementsPanier.some((e) => e.composantCommercial.identifiant === product.gencode),
                );

                const findElementPanier = findParcoursForProduct?.elementsPanier.find(
                    (e) => e.composantCommercial.identifiant === product.gencode,
                );

                const fundings = findElementPanier?.modesFinancementsElementPanier.map((fundingMode) =>
                    SapicMapper.fundingMapper(fundingMode, product.details.discounts),
                );

                if (fundings && fundings.length > 0) {
                    product.details.fundings = fundings;
                }
            });
            simulatedCart.quotes.push(simulatedQuote);
        });

        simulation.financements.modesFinancement?.forEach((funding) => {
            const fundingData = SapicMapper.fundingMapper(funding);
            if (fundingData) {
                simulatedCart.fundings.push(fundingData);
            }
        });

        return simulatedCart;
    }

    static fundingMapper(
        funding: ModesFinancement | ModesProduitFinancement,
        discounts: Promotion[] | DiscountType[] = [],
    ): IFundingDetail {
        const fundingData = funding.propositionsCommerciales[0]!;

        let { key, type } = FundingFactory.splitKey(funding.type);
        if (funding.type === 'EDP') {
            key = FundingKey.EDP;
            type = FundingType.EDP;
        }

        const odrDiscount = discounts.find((discount) => discount.type === DiscountEnumType.ODR);

        const odrValue = odrDiscount && 'value' in odrDiscount ? odrDiscount.value : (odrDiscount?.valeur ?? 0);

        return {
            label: funding.libelle,
            type,
            key,
            favorite: 'prefere' in funding ? funding.prefere : false,
            initialDeposit: fundingData.apportInitial,
            amountToFinance: fundingData.montantAFinancer,
            totalFinancingCost: fundingData.coutTotalFinancement,
            APR: fundingData.tAEG,
            interestAmount: fundingData.montantInterets,
            fixedInterestRate: fundingData.tauxDebiteurFixe,
            installments: fundingData.nbrEcheances,
            monthlyAmount: fundingData.montantMensualite,
            initialContributionWithOdr:
                odrValue > 0 ? Math.max(fundingData.apportInitial - odrValue, 1) : fundingData.apportInitial,
        };
    }

    static mapToFai(apiData: SapicProductType): FaiType {
        const product = SapicMapper.mapToProduct(apiData);
        const faiData: Partial<FaiType> = {
            downRates: apiData.debitDescendant || '',
            upRates: apiData.debitAscendant || '',
            rentalFees: apiData.fraisDeLocation || 0,
            technology: apiData.technologie as TECHNOLOGY,
            play: apiData.jeu as PLAY,
            categoryType: apiData.typeCategorieFai || '',
            obligation: apiData?.dureeEngagement || ObligationType.NONE,
            obligationLabel: SapicMapper.getObligationLabel(apiData?.dureeEngagement, true),
            commercialSeries: apiData.serieCommerciale ?? apiData.classification,
            rangeNg: apiData.gammeNg ?? 'xgbox',
            range: apiData.gamme,
            type: apiData.type,
            isDefault: appConfig.defaultPlans.simulator.fai === apiData.gencode,
            commercialPartner: apiData.partenaireCommercial,
        };
        return { ...product, ...faiData } as FaiType;
    }

    static mapToMobilePlan(apiData: SapicProductType): MobilePlanType {
        const product = SapicMapper.mapToProduct(apiData);
        const renewalPlanLabel =
            apiData.libelleEnveloppeVoix && apiData.libelleEnveloppeVoix !== 'Appels illimités'
                ? `${apiData.libelleEnveloppeVoix} ${apiData.libelleEnveloppeData}`
                : apiData.libelleEnveloppeData;

        return {
            ...product,
            gencode: apiData.gencode,
            price: apiData.prix.initial,
            fundings: [],
            typeId: SapicMapper.mapSapicTypeToPlanType(apiData.type),
            obligation: apiData?.dureeEngagement || ObligationType.NONE,
            obligationLabel: SapicMapper.getObligationLabel(apiData?.dureeEngagement, false),
            renewDelay: SapicMapper.mapSapicDelaisRenouvelabiliteToRenewDelay(apiData?.delaisRenouvelabilite),
            renewDelayLabel: SapicMapper.mapSapicDelaisRenouvelabiliteToRenewDelayLabel(apiData?.delaisRenouvelabilite),
            name: apiData.nom.replace('Avantages Smartphone', ''),
            dataEnvelope: apiData.libelleEnveloppeData || '',
            dataSize:
                Number.parseInt(apiData.libelleEnveloppeData?.match(/\b(\d+)(?:Mo|Go)\b/)?.[1] || '0') ?? undefined,
            subTypeId: (apiData.sousType as PlanSubTypeId | undefined) ?? PlanSubTypeId.CLASSIC,
            isDarwin: false,
            hasDoubleSim: false,
            hasLowPrice: false,
            voiceEnvelope: apiData.libelleEnveloppeVoix,
            isExcluPro: apiData.excluPro,
            renewalLabelDataEnvelope: renewalPlanLabel ?? '',
            isDefault: appConfig.defaultPlans.simulator.mobile === apiData.gencode,
            commercialRange: SapicMapper.mapSapicRangeToCommercialRange(apiData.gammeCommerciale),
        };
    }

    static mapToOption(apiData: SapicProductType): OptionType {
        const product = SapicMapper.mapToProduct(apiData);
        return {
            ...product,
            wordingPromo: apiData.libellePromo,
            optionCategory: apiData.categorieOption,
            smallImage: apiData.miniature,
            type: apiData.type,
            shortDescription: apiData.descriptionCourte,
            obligation: apiData.dureeEngagement,
        };
    }

    static getObligationLabel(
        obligation?: ObligationType,
        isYears: boolean = false,
        withCommitmentWording = true,
    ): string {
        switch (obligation) {
            case ObligationType.MONTHLY_24:
                return `${withCommitmentWording ? 'engagement ' : ''}${isYears ? '2 ans' : '24 mois'}`;
            case ObligationType.MONTHLY_12:
                return `${withCommitmentWording ? 'engagement ' : ''}${isYears ? '1 an' : '12 mois'}`;
            default:
                return 'sans engagement';
        }
    }

    static mapSapicRangeToCommercialRange(range?: string): PlanRange {
        switch (range) {
            case 'forfait_sans_mobile':
                return PlanRange.WITHOUT_MOBILE;
            case 'forfait_avec_mobile':
                return PlanRange.WITH_MOBILE;
            default:
                return PlanRange.DEFAULT;
        }
    }

    static mapSapicTypeToPlanType(type: string): PlanTypeId {
        const typeValues = Object.values(PlanTypeId);
        const typeValue = typeValues.find((value) => value === type);
        return typeValue || PlanTypeId.DEFAULT;
    }

    private static discountMapper(discount: Promotion): DiscountType {
        return {
            type: discount.type, // @TODO: create specific type and mapper
            value: discount.valeur,
            label: discount.identifiant,
            discount: discount.reduction,
            isIncitation: discount.estIncitation,
            isDiffered: discount.estDifferee,
            duration: discount.duree,
            marketingTypes: discount.proprietesSupplementaires?.typesMarketing ?? [],
            pennant: discount.proprietesSupplementaires?.fanion,
        };
    }

    static mapSapicDelaisRenouvelabiliteToRenewDelay(delaisRenouvelabilite?: string): RenewDelayType | undefined {
        switch (delaisRenouvelabilite) {
            case 'monthly12':
                return RenewDelayType.MONTHLY_12;
            case 'monthly24':
                return RenewDelayType.MONTHLY_24;
            default:
                return undefined;
        }
    }
    static mapSapicDelaisRenouvelabiliteToRenewDelayLabel(delaisRenouvelabilite?: string): string | undefined {
        switch (delaisRenouvelabilite) {
            case 'monthly12':
                return '12 mois';
            case 'monthly24':
                return '24 mois';
            default:
                return undefined;
        }
    }
}
